import { StandardEvents } from '@shopify/events';

/* sis-buy-summary (DS OrderSummary): the base variant row + subtotal render in
   Liquid and re-render on productSelect (conv. 11b subtree swap from
   detail.html). The extras rows arrive as sis:selection-changed events from
   the sis-studio app block (values are NUMBERS; this element owns formatting)
   and are re-applied after every swap from the cached last event.

   Instant repaint (owner report 2026-09-24: on a sign page the summary kept
   the old size while the header price moved on): a size/colour click repaints
   the selection line, price, compare and Save pill at once from the
   'sis:variant-preview' event sis-option-tiles dispatches with the matched
   variant. The server swap still follows and has the last word.

   Money: window.SisMoney (assets/sis-money.js) reproduces Liquid's
   money_without_trailing_zeros for this request - store format, decimals rule
   (the live format has no cents: $139.99 shows as $140) and the ht-money
   wrapper. Price slots take the markup, like the Liquid they replace; the Save
   pill and add-on rows take plain text, like Liquid's | strip_html.

   Missed events (owner 2026-10-16): this script loads after sis-option-tiles,
   so a load-time selection can happen before it is defined. data-variant-id
   records what the markup shows, and window.SisVariantSync (assets/
   sis-variant-sync.js) re-renders it from one section fetch whenever the
   picker's selected variant differs, so it never stays on stale HTML. */
class SisBuySummary extends HTMLElement {
  #section;
  #lastSelection = null;
  #unwatch = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, [id^="shopify-section"]') || document;
    this.#section.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section.addEventListener('sis:variant-preview', this.#onPreview);
    document.addEventListener('sis:selection-changed', this.#onSelection);
    this.#unwatch = window.SisVariantSync ? window.SisVariantSync.watch(this, (doc) => this.#swapFrom(doc)) : null;
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section?.removeEventListener('sis:variant-preview', this.#onPreview);
    document.removeEventListener('sis:selection-changed', this.#onSelection);
    this.#unwatch?.();
  }

  #onProductSelect = (event) => {
    event.promise
      .then(({ detail }) => this.#swapFrom(detail?.html))
      .catch(() => {});
  };

  // fresh base row/subtotal for the new variant, from a productSelect
  // response or a SisVariantSync re-sync fetch
  #swapFrom(doc) {
    const next = doc?.querySelector(`sis-buy-summary[data-block-id="${this.dataset.blockId}"]`);
    if (next) {
      this.dataset.price = next.dataset.price;
      this.dataset.compare = next.dataset.compare;
      if (next.dataset.variantId) this.dataset.variantId = next.dataset.variantId;
      this.innerHTML = next.innerHTML;
    }
    this.#render(); // re-apply the cached extras over the fresh subtree
  }

  // instant path: the variant sis-option-tiles just matched
  #onPreview = (event) => {
    const d = event.detail || {};
    if (String(d.productId) !== String(this.dataset.productId)) return;
    this.dataset.price = String(d.price / 100);
    this.dataset.compare = String((d.compare || d.price) / 100);
    // two apostrophes = inches on some sign products (18''); the tiles show "
    const text = (d.opts || []).map((o) => String(o).replace(/''/g, '"')).join(', ');
    const item = this.querySelector('.sis-order-summary__item, .sis-order-summary__row--base .sis-order-summary__label');
    if (item && text && item.textContent !== text) item.textContent = text;
    const baseVal = this.querySelector('.sis-order-summary__row--base .sis-order-summary__val');
    if (baseVal) this.#setMoney(baseVal, d.price / 100);
    this.#render();
  };

  #onSelection = (e) => {
    const d = (e && e.detail) || {};
    if (String(d.productId) !== String(this.dataset.productId)) return;
    this.#lastSelection = d;
    this.#render();
  };

  #text(n) {
    if (window.SisMoney) return window.SisMoney.text(n);
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  // a price slot: the store's money markup, as the Liquid it replaces
  #setMoney(el, n) {
    const html = window.SisMoney ? window.SisMoney.html(n) : this.#text(n);
    if (el.innerHTML !== html) el.innerHTML = html;
  }

  #render() {
    const holder = this.querySelector('[data-sis-extra-rows]');
    const d = this.#lastSelection;
    const hideVals = this.hasAttribute('data-hide-row-prices');
    // inline style: the chosen extras as ONE muted line, labels joined by
    // middle dots (DS summaryStyle "inline"); empty = the line stays hidden
    const inline = this.querySelector('[data-sis-extra-inline]');
    if (inline) {
      const labels = ((d && d.rows) || []).map((r) => r.label).filter(Boolean);
      inline.textContent = labels.join(' · ');
      inline.hidden = labels.length === 0;
    }
    if (holder) {
      holder.textContent = '';
      ((d && d.rows) || []).forEach((r) => {
        const row = document.createElement('div');
        row.className = 'sis-order-summary__row';
        const lab = document.createElement('span');
        lab.className = 'sis-order-summary__label';
        lab.textContent = r.label;
        row.appendChild(lab);
        // DS hidePrices hides ALL per-row values, the free "Included" ones too
        if (!hideVals) {
          if (r.free) {
            const v = document.createElement('span');
            v.className = 'sis-order-summary__val sis-order-summary__val--free';
            v.textContent = this.dataset.includedLabel || 'Included';
            row.appendChild(v);
          } else {
            const v = document.createElement('span');
            v.className = 'sis-order-summary__val';
            v.textContent = '+' + this.#text(r.value);
            row.appendChild(v);
          }
        }
        holder.appendChild(row);
      });
    }
    // subtotal and compare-at ALWAYS include the extras (spec: header price
    // and this subtotal are the only large prices and must agree)
    const extras = (d && d.extrasTotal) || 0;
    const base = parseFloat(this.dataset.price || '0');
    const cmp = parseFloat(this.dataset.compare || this.dataset.price || '0');
    const priceEl = this.querySelector('[data-sis-summary-price]');
    if (priceEl) this.#setMoney(priceEl, base + extras);
    const cmpEl = this.querySelector('[data-sis-summary-compare]');
    if (cmpEl) {
      if (cmp > base) {
        this.#setMoney(cmpEl, cmp + extras);
        cmpEl.hidden = false;
      } else {
        cmpEl.hidden = true;
      }
    }
    this.#renderSave(cmp > base ? cmp - base : 0);
  }

  // the Save pill under the subtotal: created, updated or hidden to match
  #renderSave(saved) {
    const col = this.querySelector('.sis-order-summary__total-col');
    let line = this.querySelector('.sis-order-summary__save-line');
    const template = this.dataset.saveTemplate;
    if (!saved) {
      if (line) line.hidden = true;
      return;
    }
    if (!template || !col) return;
    if (!line) {
      line = document.createElement('span');
      line.className = 'sis-order-summary__save-line';
      const pill = document.createElement('span');
      pill.className = 'sis-order-summary__save';
      line.appendChild(pill);
      col.appendChild(line);
    }
    line.hidden = false;
    const pill = line.querySelector('.sis-order-summary__save');
    const t = template.replace('__SIS_AMOUNT__', this.#text(saved));
    if (pill && pill.textContent !== t) pill.textContent = t;
  }
}

if (!customElements.get('sis-buy-summary')) {
  customElements.define('sis-buy-summary', SisBuySummary);
}
